import * as React from 'react';

import { Command } from 'pos/common';
import { startOfDay, isEqual } from 'date-fns';
import { SessionProvider, Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Res, Permission } from 'core/api';
import { AcceptActionButton, ActionButton, BackActionButton, Content, Filler, Footer, Header, LargeDeviceOnly, PhoneOnly, PosScreen } from 'pos/ui';
import { Currency } from 'core/utils';

import { CashdrawerFacade } from 'pos/facades';
import { CashControl } from 'pos/services';
import { Balance } from 'pos/modules/helpers/cashdrawer';
import { Message, Modal } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { BasicButton, useEffectOnce } from 'core/ui-next';
import { Box, VStack } from '@chakra-ui/react';
import { z } from 'zod';
import { balanceSchema } from 'pos/modules/helpers/cashdrawer/Balance';
import { InputIconButton } from 'core/ui-next/controls/Input';
import { EditDenominationsScreen } from 'pos/modules/helpers/cashdrawer/EditDenominationsScreen';
import * as Images from 'pos/Images';
import { zBusinessDay, zMonetary } from 'core/infrastructure-next';
import * as UI from 'core/ui';

type Props = {
	initialAmount: number;
	systemBusinessDay: Nullable<Date>;
} & Modal.Actions<Date>;

const stateSchema = z.object({
	businessDay: zBusinessDay(),
	initialAmount: zMonetary({ nonNegative: true, required: true }),
	denominations: balanceSchema
});

type State = z.infer<typeof stateSchema>;

(window as any).z = z;

async function openDay(systemBusinessDay: Nullable<Date>, businessDay: Date, initialAmount: number): Promise<boolean> {

	const today = startOfDay(new Date());
	const isUsingDefaultDate = systemBusinessDay != null;

	const isOpeningInDifferentDay = !isUsingDefaultDate && !isEqual(businessDay, today);

	if (isOpeningInDifferentDay && !SessionProvider.hasPermission(Permission.Pos_OpenDayWithDifferentDate)) {
		await Message.show(Res.Msg.MSG_PERMISSION_IS_REQUIRED_TO_OPEN_DAY_WITH_PAST_OR_FUTURE_DATE());
		return false;
	}

	if (isOpeningInDifferentDay) {
		const msg = Res.Msg.MSG_CONFIRM_OPEN_DAY_IN_DIFFERENT_BUSINESS_DAY(businessDay, today);
		const result = await Message.confirm(msg);
		if (!result.ok)
			return false;
	}

	try {
		await CashdrawerFacade.openDay(systemBusinessDay, businessDay, initialAmount);
		return true;
	}
	catch (error: any) {
		await Message.show(error.message);
		return false;
	}
}

async function editDenominations(balance: Balance.Balance): Promise<Result<Balance.Balance>> {
	return Modal.show(EditDenominationsScreen, { balance, contentDescriptionTitle: Res.Web.EDIT_DENOMINATIONS_DESCRIPTION_OPEN_DAY_TXT() });
}

function OpenDayScreen(props: Props) {

	const { initialAmount, systemBusinessDay, cancel, close } = props;
	const today = startOfDay(new Date());
	const [openDayState, setOpenDayState] = React.useState<State>({
		businessDay: systemBusinessDay || today,
		initialAmount: initialAmount,
		denominations: Balance.zero()
	});
	const formRef = React.useRef<FormRef<State>>(null);
	const { Form, DateInput, DecimalInput } = useControlsFor<State>();

	const makeCashBalance = async () => {

		const result = await editDenominations(openDayState.denominations);

		if (result.ok) {
			const total = Balance.total(result.value);

			setOpenDayState({ ...openDayState, initialAmount: total, denominations: result.value });
			formRef.current!.setValue('initialAmount', total);
		}
	};

	const onAccept = async () => {

		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		const ok = await openDay(systemBusinessDay, result.value.businessDay, result.value.initialAmount);
		if (ok)
			close(result.value.businessDay);

	};

	const onShowStatus = async () => {

		if (!SessionProvider.getCurrent().UseCashControl) {
			await Command.execute('openCashDrawer');
			return;
		}

		await CashControl.showOperations();
		const result = await CashControl.getCurrentAmount();
		if (!result.ok) {
			await Message.show(Res.Msg.MSG_CANNOT_READ_CURRENT_AMOUNT_FROM_CASH_CONTROL());
			return;
		}

		setOpenDayState({ ...openDayState, initialAmount: result.value });
		formRef.current!.setValue('initialAmount', result.value);
	};


	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	useEffectOnce(() => {
		if (
			!SessionProvider.getCurrent().UseCashControl &&
			SessionProvider.getCurrent().AllowCashTransactions
		) {
			formRef.current?.setFocus?.('initialAmount');
		}
	});

	const disabledCashBalanceInput = !SessionProvider.getCurrent().AllowCashTransactions || SessionProvider.getCurrent().UseCashControl;

	return <PosScreen screenId='OPEN_DAY' size={'small'} useKeyPad={true}>

		<Header title={Res.Web.OPEN_DAY()} onClose={cancel} />

		<Content>
			<Box paddingX='6' paddingTop='14' paddingBottom={'4'} height={'full'} overflowY={'auto'}>
				<Form initialValue={{ businessDay: systemBusinessDay || today, initialAmount, denominations: Balance.zero() }} schema={stateSchema} ref={formRef}>
					<VStack gap='9'>
						<DateInput label={Res.Web.OPEN_DAY_BUSINESS_DAY_LBL()} linkTo='businessDay' disabled={systemBusinessDay !== null} />
						{
							!SessionProvider.getCurrent().UseCashControlBlindOpening ?
								<DecimalInput
									label={Res.Web.OPEN_DAY_INITIAL_AMOUNT_LBL(Currency.SYMBOL)}
									linkTo='initialAmount'
									endElement={<InputIconButton onClick={() => makeCashBalance()} icon={Images.MoneyEdit} disabled={disabledCashBalanceInput} />}
									disabled={disabledCashBalanceInput}
									onKeyDown={UI.whenEnterKey(onAccept)}
								/> : null
						}
					</VStack>
				</Form>

				<LargeDeviceOnly>
					<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onAccept} marginTop='6' />
				</LargeDeviceOnly>
			</Box>

		</Content>


		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
				{SessionProvider.getCurrent().UseCashControlBlindOpening ? null : <ActionButton action={onShowStatus} title={Res.Web.OPEN_CASH_DRAWER_BTN()} image={Images.OpenCashDrawer} />}
				<AcceptActionButton action={onAccept} />
			</PhoneOnly>

			<LargeDeviceOnly>
				<Filler />
				{SessionProvider.getCurrent().UseCashControlBlindOpening ? null : <ActionButton action={onShowStatus} title={Res.Web.OPEN_CASH_DRAWER_BTN()} image={Images.OpenCashDrawer} />}
			</LargeDeviceOnly>
		</Footer>


	</PosScreen>;
};

declare global {
	interface CommandType {
		openDay: () => Promise<Result>
	}
}

Command.register('openDay', Preconditions.ensure(Permission.Pos_OpenDay), async (): Promise<Result> => {

	const openDayParams = await CashdrawerFacade.getOpenDayParams();
	const systemBusinessDay = openDayParams.SystemBusinessDay;

	let defaultInitialAmount = openDayParams.DefaultInitialAmount;
	if (SessionProvider.getCurrent()!.UseCashControl) {

		const result = await CashControl.getCurrentAmount();
		if (!result.ok) {
			await Message.show(Res.Msg.MSG_CANNOT_READ_CURRENT_AMOUNT_FROM_CASH_CONTROL());
			return Result.error();
		}

		defaultInitialAmount = result.value;
	}

	return Modal.show(OpenDayScreen, { systemBusinessDay: systemBusinessDay, initialAmount: defaultInitialAmount });
});


